import React, { forwardRef, useMemo } from 'react';
import type { SingleAutoCompleteOption } from './AutoCompleteSelect';
import AutoCompleteSelect from './AutoCompleteSelect';
import type { LocationId, Region, ZoneId } from '../graphql';
import { useAllRegionsQuery, useAllZonesQuery } from '../graphql';
import type { ScenarioLocation } from '../lib/planningData';
import { useAllLocations } from '../lib/allLocations';
import { useCurrentUser } from '../state/zustand';
import compact from 'lodash/compact';
import { getPortName } from '../lib/getters';

export type LocationSelectValue = {
  value?: number | null;
  label?: string | null;
  location?: Partial<ScenarioLocation> | null;
};

export interface LocationSelectProps<ValueType> {
  onChange?: (v: ValueType | undefined) => void;
  value?: ValueType;
  isClearable?: boolean;
  isDisabled?: boolean;
  'aria-label'?: string;
  skipIds?: Set<number>;
  skipInvalid?: boolean;
  required?: boolean;
  name?: string;
  maxHeight?: number;
}

export const isLocationCoordinatesInvalid = (
  location: Pick<ScenarioLocation, 'latitude' | 'longitude'>,
) => !location.latitude || !location.longitude;

const LocationSelect = forwardRef<
  unknown,
  LocationSelectProps<LocationSelectValue>
>(function LocationSelect(
  { onChange, value, skipIds, skipInvalid = true, ...restProps },
  _ref,
): JSX.Element {
  const currentUser = useCurrentUser();
  const configUseMasterPorts = currentUser?.organization?.configUseMasterPorts;

  const { data, loading } = useAllLocations();

  const fetchedOptions = useMemo(
    () =>
      compact(data?.allLocations?.nodes)
        .filter(
          (n) =>
            !!(
              !skipIds?.has(n.id) &&
              (skipInvalid
                ? !isLocationCoordinatesInvalid(n) && n.countryId
                : true) &&
              (configUseMasterPorts || value?.value === n.id
                ? true
                : n.rootOrg !== null)
            ),
        )
        .map((location) => ({
          value: location?.id,
          label: getPortName(location),
          location,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [
      data?.allLocations?.nodes,
      skipInvalid,
      skipIds,
      configUseMasterPorts,
      value?.value,
    ],
  );

  const options: SingleAutoCompleteOption[] =
    fetchedOptions.length > 0
      ? fetchedOptions
      : value
        ? [{ value: value.value ?? -1, label: value.label ?? '' }]
        : [];

  const onSelectLocation = (v: number | null) => {
    const location = options.find((x) => x.value === v);
    if (onChange) onChange(location);
  };

  return (
    <AutoCompleteSelect
      aria-label="locations"
      isLoading={loading}
      options={options}
      value={value ? value.value : null}
      onChange={(v) => onSelectLocation(v)}
      {...restProps}
    />
  );
});

export type RegionSelectValue = {
  value?: number | null;
  label?: string | null;
  region?: Region | null;
};

export const RegionSelect = ({
  onChange,
  value,
  skipIds,
  ...restProps
}: LocationSelectProps<RegionSelectValue>): JSX.Element => {
  const { data, loading } = useAllRegionsQuery();

  const fetchedOptions = useMemo(
    () =>
      compact(data?.allRegions?.nodes)
        .filter((n) => !!!skipIds?.has(n.id))
        .map((region) => ({
          value: region.id,
          label: region.name ?? '',
          region,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [data?.allRegions?.nodes, skipIds],
  );

  const options: SingleAutoCompleteOption[] =
    fetchedOptions.length > 0
      ? fetchedOptions
      : value
        ? [{ value: value.value ?? -1, label: value.label ?? '' }]
        : [];

  const onSelectRegion = (v: number | null) => {
    const region = options.find((x) => x.value === v);
    if (onChange) onChange(region);
  };

  return (
    <AutoCompleteSelect
      aria-label="regions"
      isLoading={loading}
      options={options}
      value={value ? value.value : null}
      onChange={(v) => onSelectRegion(v)}
      {...restProps}
    />
  );
};

export type LocationZoneSelectValue =
  | {
      label: string;
      type: 'location';
      /** Id of location */
      value: LocationId;
    }
  | {
      label: string;
      type: 'zone';
      /** Id of zone */
      value: ZoneId;
    };

export interface LocationZoneSelectProps {
  'aria-label'?: string;
  isDisabled?: boolean;
  name?: string;
  onChange?: (v: LocationZoneSelectValue | undefined) => void;
  required?: boolean;
  skipInvalidLocations?: boolean;
  skipLocationIds?: Set<number>;
  skipZoneIds?: Set<number>;
  value?: LocationZoneSelectValue;
}

export function LocationZoneSelect({
  onChange,
  skipInvalidLocations: skipInvalid = true,
  skipLocationIds,
  skipZoneIds,
  value,
  ...restProps
}: LocationZoneSelectProps): JSX.Element {
  const currentUser = useCurrentUser();
  const configUseMasterPorts = currentUser?.organization?.configUseMasterPorts;

  const { data: locationsData, loading: locationsLoading } = useAllLocations();
  const { data: zonesData, loading: zonesLoading } = useAllZonesQuery();

  const locationOptions = useMemo(
    () =>
      compact(locationsData?.allLocations?.nodes)
        .filter(
          (n) =>
            !!(
              !skipLocationIds?.has(n.id) &&
              (skipInvalid ? n.latitude && n.latitude && n.countryId : true) &&
              (configUseMasterPorts || value?.value === n.id
                ? true
                : n.rootOrg !== null)
            ),
        )
        .map((location) => ({
          id: location.id,
          label: getPortName(location),
          type: 'location' as const,
          value: `location-${location.id}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [
      locationsData?.allLocations?.nodes,
      skipInvalid,
      skipLocationIds,
      configUseMasterPorts,
      value?.value,
    ],
  );

  const zoneOptions = useMemo(() => {
    return compact(zonesData?.allZones?.nodes)
      .filter((zone) => !skipZoneIds?.has(zone.id))
      .map((zone) => {
        return {
          id: zone.id,
          label: zone.name ?? '',
          type: 'zone' as const,
          value: `zone-${zone.id}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [skipZoneIds, zonesData]);

  const options: {
    label: string;
    options: (
      | {
          id: LocationId;
          label: string;
          type: 'location';
          value: string;
        }
      | {
          id: ZoneId;
          label: string;
          type: 'zone';
          value: string;
        }
    )[];
  }[] =
    locationOptions.length > 0
      ? [
          {
            label: 'Zones',
            options: zoneOptions,
          },
          {
            label: 'Locations',
            options: locationOptions,
          },
        ]
      : value
        ? value.type === 'location'
          ? [
              {
                label: 'Locations',
                options: [
                  {
                    id: value.value,
                    label: value.label,
                    type: value.type,
                    value: `${value.type}-${value.value}`,
                  },
                ],
              },
            ]
          : [
              {
                label: 'Zones',
                options: [
                  {
                    id: value.value,
                    label: value.label,
                    type: value.type,
                    value: `${value.type}-${value.value}`,
                  },
                ],
              },
            ]
        : [];

  const onSelectLocation = (newValue: string | null) => {
    const selectedOption = options
      .flatMap((group) => group.options)
      .find((x) => x.value === newValue);

    if (onChange)
      onChange(
        selectedOption
          ? selectedOption.type === 'zone'
            ? {
                label: selectedOption.label,
                type: 'zone',
                value: selectedOption.id,
              }
            : {
                label: selectedOption.label,
                type: 'location',
                value: selectedOption.id,
              }
          : undefined,
      );
  };

  return (
    <AutoCompleteSelect<string>
      aria-label="locations"
      isLoading={locationsLoading || zonesLoading}
      options={options}
      value={value ? `${value.type}-${value.value}` : null}
      onChange={(v) => onSelectLocation(v)}
      {...restProps}
    />
  );
}

export default LocationSelect;
